import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '@/components/base/Button';
import { useRole } from '@/context/RoleContext';
import { API_MODE, api, apiList } from '@/utils/api';
import { formatDate } from '@/utils/format';
import type { FundingWallet, SinglePayee, SinglePayment, Wallet } from '@/types/portal';
import {
  appendSavedPayee,
  loadSavedPayees,
  persistSavedPayees,
} from '@/utils/payees';
import SendMoneySheet from './components/SendMoneySheet';
import PayBillSheet from './components/PayBillSheet';
import BuyAirtimeSheet from './components/BuyAirtimeSheet';
import BuyGoodsSheet from './components/BuyGoodsSheet';
import RecentSinglePayments from './components/RecentSinglePayments';
import PaymentConfirmModal from './components/PaymentConfirmModal';
import PaymentOtpModal from './components/PaymentOtpModal';
import type { PaymentSentMeta } from './types';
import { PLATFORM } from '@/config/platform';

const messageOf = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);

/** The API sends an ISO timestamp; the list shows "16 Sep 2026". */
const forDisplay = (payment: SinglePayment): SinglePayment => ({ ...payment, createdAt: formatDate(payment.createdAt) });

type FlowType = 'mobile' | 'bank' | 'wallet' | 'bill' | 'airtime' | 'goods';

const FLOW_OPTIONS: { id: FlowType; label: string; icon: string; description: string; tag?: string }[] = [
  {
    id: 'wallet',
    label: `${PLATFORM.payName} Wallet`,
    icon: 'ri-wallet-3-line',
    description: 'Instant wallet-to-wallet transfer',
    tag: 'Popular',
  },
  {
    id: 'mobile',
    label: 'Send to mobile wallet',
    icon: 'ri-smartphone-line',
    description: 'M-Pesa, Airtel Money, Tigo Pesa, MTN MoMo',
  },
  {
    id: 'bank',
    label: 'Send to bank',
    icon: 'ri-bank-line',
    description: 'Any bank, worldwide',
  },
  {
    id: 'goods',
    label: 'Buy goods',
    icon: 'ri-shopping-bag-3-line',
    description: 'Pay a merchant or till number',
  },
  {
    id: 'bill',
    label: 'Pay a bill',
    icon: 'ri-file-list-2-line',
    description: 'Utilities, paybills & service accounts',
  },
  {
    id: 'airtime',
    label: 'Buy airtime',
    icon: 'ri-sim-card-line',
    description: 'Top up any mobile line with airtime',
  },
];

export default function SinglePaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { can } = useRole();
  const canManage = can('payments.manage');
  const canApprove = can('payments.approve');
  // Production has no approval queue and pays as soon as the OTP is entered (BACKEND_GAPS #64, #65),
  // so only approvers may send there.
  const sendBlocked = API_MODE === 'agency' && !canApprove;

  // Live payee directory: platform payees plus any the user has saved.
  const [addedPayees, setAddedPayees] = useState<SinglePayee[]>(() => loadSavedPayees());
  const [directory, setDirectory] = useState<SinglePayee[]>([]);
  // A creator opened from a Pay button (?creator=&find=), looked up by name or email.
  const [linkedPayee, setLinkedPayee] = useState<SinglePayee | null>(null);
  const payees = useMemo(
    () => [...directory, ...addedPayees, ...(linkedPayee ? [linkedPayee] : [])],
    [directory, addedPayees, linkedPayee],
  );
  const [wallets, setWallets] = useState<FundingWallet[]>([]);
  const [error, setError] = useState('');

  // Funding wallets, the payee directory and recent payments.
  useEffect(() => {
    apiList<Wallet>('/wallets?ownerType=Funding')
      .then(({ data }) =>
        setWallets(
          data
            .filter((wallet) => wallet.status === 'Wallet active')
            .map((wallet) => ({
              id: wallet.id,
              ownerType: 'Funding',
              currency: wallet.currency,
              balance: wallet.balance,
              status: wallet.status,
              rail: wallet.market,
            })),
        ),
      )
      .catch((loadError: unknown) => setError(messageOf(loadError, 'Could not load the wallets.')));
    apiList<SinglePayee>('/payees?per_page=100')
      .then(({ data }) => setDirectory(data))
      .catch(() => setDirectory([]));
    apiList<SinglePayment>('/payments?per_page=100')
      .then(({ data }) => setPayments(data.map(forDisplay)))
      .catch(() => setPayments([]));
  }, []);

  // Deep-link support: /payments/send?creator=<creatorId>&find=<name or email> preselects the payee.
  const creatorId = searchParams.get('creator');
  const find = searchParams.get('find') ?? '';
  useEffect(() => {
    if (!creatorId || find.trim().length < 2) return;
    let cancelled = false;
    apiList<SinglePayee>(`/payees/wallet-holders?q=${encodeURIComponent(find)}`)
      .then(({ data }) => !cancelled && setLinkedPayee(data.find((item) => item.creatorId === creatorId) ?? null))
      .catch(() => !cancelled && setLinkedPayee(null));
    return () => {
      cancelled = true;
    };
  }, [creatorId, find]);
  const initialPayee = creatorId
    ? payees.find((item) => item.creatorId === creatorId) ?? null
    : null;
  // Opened from a campaign's Pay button: the roster row the payment is for, and its status.
  const campaignCreatorId = searchParams.get('cc') ?? '';
  const campaignCreatorStatus = searchParams.get('ccStatus') ?? '';
  const campaignName = searchParams.get('campaign') ?? 'the campaign';
  // A campaign creator can be paid only once payable (all their content approved).
  const notEligible = campaignCreatorId !== '' && campaignCreatorStatus !== 'payable';

  // Deep-link support: /payments/send?flow=bank jumps straight to a flow.
  const flowParam = searchParams.get('flow');
  const initialFlow: FlowType | null =
    flowParam === 'mobile' ||
    flowParam === 'bank' ||
    flowParam === 'wallet' ||
    flowParam === 'bill' ||
    flowParam === 'airtime' ||
    flowParam === 'goods'
      ? flowParam
      : null;

  const [activeFlow, setActiveFlow] = useState<FlowType | null>(() => {
    if (initialPayee) {
      if (initialPayee.target.mobileNumber) return 'mobile';
      if (initialPayee.target.accountNumber) return 'bank';
      if (initialPayee.target.walletId) return 'wallet';
      return null;
    }
    return initialFlow;
  });

  // The directory loads after the first render: open the deep-linked payee's flow once it arrives.
  const [deepLinked, setDeepLinked] = useState(false);
  useEffect(() => {
    if (deepLinked || !initialPayee || activeFlow) return;
    setDeepLinked(true);
    if (initialPayee.target.mobileNumber) setActiveFlow('mobile');
    else if (initialPayee.target.accountNumber) setActiveFlow('bank');
    else if (initialPayee.target.walletId) setActiveFlow('wallet');
  }, [deepLinked, initialPayee, activeFlow]);
  const [payments, setPayments] = useState<SinglePayment[]>([]);
  const [counter, setCounter] = useState(1);
  // A payment waiting for its OTP: the sheet's draft and what the API was asked to send.
  const [pending, setPending] = useState<{ draft: SinglePayment; meta: PaymentSentMeta; body: object } | null>(null);
  const [otpError, setOtpError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sentPayment, setSentPayment] = useState<SinglePayment | null>(null);
  const [sentMeta, setSentMeta] = useState<PaymentSentMeta | null>(null);
  const [payeeSaved, setPayeeSaved] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [notice, setNotice] = useState('');

  // The sheet's draft carries the labels; the API decides the reference and status.
  const finish = (draft: SinglePayment, meta: PaymentSentMeta, sent: SinglePayment) =>
    handlePaymentSent(
      forDisplay({ ...draft, ...sent, reference: sent.reference || '—', payeeName: draft.payeeName, target: draft.target, rail: draft.rail, avatar: draft.avatar || sent.avatar }),
      meta,
    );

  // Submit → production texts an OTP (or pays at once) → the OTP modal confirms it.
  const submitPayment = async (draft: SinglePayment, meta: PaymentSentMeta) => {
    if (busy) return;
    if (sendBlocked) {
      setError('Only an approver (Admin or Finance) can send payments.');
      return;
    }
    if (notEligible) {
      setError(`${initialPayee?.name ?? 'This creator'} isn't eligible to be paid on ${campaignName} yet.`);
      return;
    }
    setBusy(true);
    setError('');
    const destination = (meta.request as { destination?: { walletId?: string } }).destination;
    const forLinkedCreator =
      campaignCreatorId !== '' && initialPayee !== null && destination?.walletId === initialPayee.target.walletId;
    const body = {
      channel: draft.channel,
      amount: draft.amount,
      currency: draft.currency,
      payeeName: draft.payeeName,
      ...meta.request,
      ...(forLinkedCreator ? { campaignCreatorId } : {}),
    };
    try {
      const result = await api<{ requiresOtp: boolean; payment?: SinglePayment }>('/payments/otp', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!result.requiresOtp && result.payment) finish(draft, meta, result.payment);
      else {
        setOtpError('');
        setPending({ draft, meta, body });
      }
    } catch (submitError) {
      setError(messageOf(submitError, 'Could not send the payment.'));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (otp: string) => {
    if (!pending || busy) return;
    setBusy(true);
    setOtpError('');
    try {
      const sent = await api<SinglePayment>('/payments', {
        method: 'POST',
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body: JSON.stringify({ ...pending.body, otp }),
      });
      setPending(null);
      finish(pending.draft, pending.meta, sent);
    } catch (verifyError) {
      setOtpError(messageOf(verifyError, 'Could not verify the code.'));
    } finally {
      setBusy(false);
    }
  };

  const handlePaymentSent = (payment: SinglePayment, meta: PaymentSentMeta) => {
    setPayments((prev) => [payment, ...prev]);
    setCounter((c) => c + 1);
    setSentPayment(payment);
    setSentMeta(meta);
    setPayeeSaved(false);
    setSuccessOpen(true);
    setNotice(`${payment.reference} submitted — ${payment.status.toLowerCase()}.`);
    setActiveFlow(null);
  };

  const handleSavePayee = () => {
    const newPayee = sentMeta?.newPayee;
    if (!newPayee || payeeSaved) return;
    setAddedPayees((prev) => {
      const next = appendSavedPayee(prev, newPayee);
      persistSavedPayees(next);
      return next;
    });
    setPayeeSaved(true);
    setNotice(`${newPayee.name} saved to your payee directory — they'll appear in search next time.`);
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
    setSentPayment(null);
    setSentMeta(null);
    setPayeeSaved(false);
  };

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6 md:py-8">
      {/* Page header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            Single Payment
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            Send one payment to a mobile wallet or bank account, anywhere in the world.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            icon="ri-exchange-dollar-line"
            onClick={() => navigate('/payments/transactions')}
          >
            Transactions
          </Button>
          <Button
            icon="ri-stack-line"
            onClick={() => navigate('/payments/bulk')}
            disabled={!canManage}
          >
            Bulk payments
          </Button>
        </div>
      </div>

      {notice ? (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="cursor-pointer text-accent-700 transition-colors hover:text-accent-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {sendBlocked ? (
        <p className="mt-5 flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2.5 text-sm text-secondary-900">
          <i className="ri-lock-line mt-0.5" />
          Only an approver (Admin or Finance) can send payments. Your role can see this page, but payments
          aren&apos;t held for approval yet, so the send buttons won&apos;t work for you.
        </p>
      ) : null}

      {campaignCreatorId && initialPayee ? (
        campaignCreatorStatus === 'payable' ? (
          <p className="mt-5 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5 text-sm text-accent-900">
            <i className="ri-links-line mt-0.5" />
            This payment is recorded against {campaignName}, and {initialPayee.name} is marked Paid there.
          </p>
        ) : (
          <p className="mt-5 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-800">
            <i className="ri-close-circle-line mt-0.5" />
            {initialPayee.name} isn&apos;t eligible to be paid on {campaignName} yet: all their content has to be
            approved first. Check the campaign&apos;s Payment tab for the reason.
          </p>
        )
      ) : null}

      {error ? (
        <div
          role="alert"
          className="mt-5 flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5"
        >
          <span className="inline-flex items-center gap-2 text-sm text-primary-800">
            <i className="ri-error-warning-line" />
            {error}
          </span>
          <button
            type="button"
            onClick={() => setError('')}
            className="cursor-pointer text-primary-700 transition-colors hover:text-primary-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {!canManage ? (
        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-600">
          <i className="ri-lock-line" />
          Your role can view payments but not send them — switch to Finance or Administrator.
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Left column: flow picker + active sheet */}
        <div className="lg:col-span-5">
          {/* Flow type picker */}
          {!activeFlow ? (
            <section className="rounded-lg border border-background-200 bg-background-50">
              <header className="flex items-center gap-2 border-b border-background-200 px-5 py-4">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-base text-primary-700">
                  <i className="ri-send-plane-line" />
                </span>
                <div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950">
                    Choose transfer type
                  </h3>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    Select how you want to send the payment
                  </p>
                </div>
              </header>
              <ul className="divide-y divide-background-100">
                {FLOW_OPTIONS.map((option) => {
                  const isPopular = Boolean(option.tag);
                  return (
                    <li key={option.id}>
                      <button
                        type="button"
                        disabled={!canManage}
                        onClick={() => setActiveFlow(option.id)}
                        className={`choice-row flex w-full cursor-pointer items-center gap-4 px-5 py-4 text-left hover:pl-6 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:pl-5 ${
                          isPopular
                            ? 'bg-primary-50 hover:bg-primary-100'
                            : 'hover:bg-background-100'
                        }`}
                      >
                        <span
                          className={`choice-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xl ${
                            isPopular
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-background-200 text-foreground-700'
                          }`}
                        >
                          <i className={option.icon} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="block text-sm font-medium text-foreground-950">
                              {option.label}
                            </span>
                            {option.tag ? (
                              <span className="badge-shimmer inline-flex items-center rounded-full bg-primary-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-background-50">
                                {option.tag}
                              </span>
                            ) : null}
                          </span>
                          <span className="block text-xs text-foreground-500">
                            {option.description}
                          </span>
                        </span>
                        <i className="choice-arrow ri-arrow-right-s-line text-lg text-foreground-400" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : activeFlow === 'bill' ? (
            <section className="rounded-lg border border-background-200 bg-background-50 px-5 py-5">
              <PayBillSheet
                wallets={wallets}
                canManage={canManage}
                canApprove={canApprove}
                counter={counter}
                onPaymentSent={(draft, meta) => void submitPayment(draft, meta)}
                onClose={() => setActiveFlow(null)}
              />
            </section>
          ) : activeFlow === 'airtime' ? (
            <section className="rounded-lg border border-background-200 bg-background-50 px-5 py-5">
              <BuyAirtimeSheet
                wallets={wallets}
                canManage={canManage}
                canApprove={canApprove}
                counter={counter}
                onPaymentSent={(draft, meta) => void submitPayment(draft, meta)}
                onClose={() => setActiveFlow(null)}
              />
            </section>
          ) : activeFlow === 'goods' ? (
            <section className="rounded-lg border border-background-200 bg-background-50 px-5 py-5">
              <BuyGoodsSheet
                wallets={wallets}
                canManage={canManage}
                canApprove={canApprove}
                counter={counter}
                onPaymentSent={(draft, meta) => void submitPayment(draft, meta)}
                onClose={() => setActiveFlow(null)}
              />
            </section>
          ) : (
            <section className="rounded-lg border border-background-200 bg-background-50 px-5 py-5">
              <SendMoneySheet
                channel={activeFlow}
                wallets={wallets}
                payees={payees}
                canManage={canManage}
                canApprove={canApprove}
                counter={counter}
                onPaymentSent={(draft, meta) => void submitPayment(draft, meta)}
                onClose={() => setActiveFlow(null)}
                initialPayeeId={initialPayee?.id ?? null}
              />
            </section>
          )}
        </div>
      </div>

      {/* Recent payments */}
      <div className="mt-6">
        <RecentSinglePayments payments={payments} />
      </div>

      <PaymentOtpModal
        open={pending !== null}
        payeeName={pending?.draft.payeeName ?? ''}
        amount={pending?.draft.amount ?? 0}
        currency={pending?.draft.currency ?? ''}
        busy={busy}
        error={otpError}
        onVerify={(otp) => void verifyOtp(otp)}
        onCancel={() => {
          setPending(null);
          // Hands back the amount the backend took from the wallet when it sent the code.
          void api('/payments/otp/cancel', { method: 'POST' }).catch(() => undefined);
        }}
      />

      {/* Success modal */}
      {sentPayment ? (
        <PaymentConfirmModal
          open={successOpen}
          onClose={handleSuccessClose}
          payee={null}
          channel={sentPayment.channel}
          invoice={null}
          targetLabel={sentPayment.target}
          rail={null}
          wallet={null}
          currency={sentPayment.currency}
          amount={sentPayment.amount}
          fee={0}
          totalDebit={sentPayment.amount}
          reference={sentPayment.reference}
          canApprove={canApprove}
          sent={sentPayment}
          newPayee={sentMeta?.newPayee ?? null}
          payeeSaved={payeeSaved}
          onSavePayee={handleSavePayee}
          onConfirm={() => {}}
        />
      ) : null}
    </div>
  );
}